'use client';

import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverFacetsQuery } from '@loci/api';

import { Chip } from '@/components/atoms/Chip';
import { cn } from '@/components/atoms/primitives/utils';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import { formatCount } from '@/lib/discover/formatCount';
import {
  PRICE_PRESETS,
  PRICE_PRESET_LABELS,
  derivePricePresetFacetCounts,
  type PricePreset,
} from '@/lib/discover/pricePreset';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';

/**
 * DiscoverPricePresets — Discover filter rail molecule (WEB-656 / DPL-06,
 * disabled-zero-count treatment added by WEB-730).
 *
 * Replaces the retired continuous `PriceRangeSlider` with three mutually
 * exclusive presets — **Free / $0–25 / $25+**. A slider implied the catalogue
 * supports arbitrary price cuts; in practice most events have no published
 * price at all (`DiscoverEvent.priceKnown === false`), so a slider was
 * precision theatre over sparse data and a poor touch target in the mobile
 * sheet.
 *
 * Selection semantics:
 *   - The three options are mutually exclusive — selecting one deselects any
 *     other.
 *   - Clicking the already-active preset clears it (returns to "no price
 *     filter").
 *   - A preset leaves out events with no listed price, but never silently
 *     (WEB-1230, ruling R6): a "+N not listed" toggle under the presets shows
 *     how many there are (`facets.priceUnlisted`, same response) and puts
 *     them back (`includeUnpriced=true`). Clearing the preset clears it too.
 *     See `lib/discover/pricePreset.ts`.
 *
 * **Disabled zero-count treatment (WEB-730):** a preset whose live facet
 * bucket count is confirmed `0` renders disabled — non-interactive to both
 * pointer and keyboard, `aria-disabled="true"` — instead of a clickable dead
 * end that can only ever return an empty state. Mirrors the WEB-659 / DPL-09
 * pattern already shipped on `DiscoverFacetChecklistView`
 * (`disableZeroCount`). Counts are derived from the discover response's
 * inline `facets.priceHistogram` via {@link derivePricePresetFacetCounts} —
 * the SAME shared `/events/discover` subscription every other rail molecule
 * reads, never a second request (the cold-load single-fetch invariant).
 *
 * The exported {@link DiscoverPricePresets} is the *Connected* component
 * wired into the canonical `/discover` URL state (`useDiscoverParams`). The
 * underlying *View* is exported as {@link DiscoverPricePresetsView} for
 * Storybook and direct unit testing.
 */

/** Default caption replacing the preset label's usual layout when disabled. */
const DEFAULT_ZERO_COUNT_LABEL = 'No active events';

export interface DiscoverPricePresetsViewProps {
  /** Currently active preset, or `undefined` when no price filter is set. */
  value: PricePreset | undefined;
  /** Called with the next value after a preset is clicked (toggle semantics). */
  onChange: (next: PricePreset | undefined) => void;
  /**
   * Presets confirmed (via live facet data) to return zero events. Each
   * renders disabled — `disabled` + `aria-disabled="true"`, not clickable via
   * pointer or keyboard — instead of a dead-end control (WEB-730). Defaults
   * to an empty set so existing callers (stories, unit tests) keep today's
   * fully-interactive behavior unchanged.
   *
   * A preset that is BOTH the active `value` AND zero-count still renders
   * `aria-pressed="true"` + disabled — the selection is preserved (e.g. a
   * date-preset change dropped a previously-picked preset to zero), not
   * silently cleared, but it can no longer be toggled from within this
   * control. Removal happens via the separate `DiscoverActiveFilters` chip
   * strip, which reads/writes URL state directly and is unaffected by this
   * disabled treatment.
   */
  disabledPresets?: ReadonlySet<PricePreset>;
  /**
   * Caption shown in place of the preset label for a disabled zero-count
   * button. Falls back to `"No active events"` when omitted.
   */
  zeroCountLabel?: string;
  /**
   * WEB-1230 — events in the current result with no listed price
   * (`facets.priceUnlisted`). `undefined` = not known yet (loading, error, or
   * an older payload); the toggle then stays hidden rather than claiming `0`.
   */
  unlistedCount?: number;
  /** WEB-1230 — whether the "+N not listed" toggle is on. */
  includeUnlisted?: boolean;
  /** WEB-1230 — called with the toggle's next state. */
  onIncludeUnlistedChange?: (next: boolean) => void;
  /** Optional id used to associate the legend with the button group. */
  id?: string;
  /** Wrapper class names. */
  className?: string;
}

/**
 * Whether the "+N not listed" toggle renders: only under an active preset
 * (with none, nothing is filtered), only with a known count, and only when
 * there is something to include — unless it is already on, so the visitor can
 * always switch it back off.
 */
export function shouldShowUnlistedToggle(
  value: PricePreset | undefined,
  unlistedCount: number | undefined,
  includeUnlisted: boolean,
): boolean {
  if (value === undefined || unlistedCount === undefined) return false;
  return unlistedCount > 0 || includeUnlisted;
}

const EMPTY_DISABLED_PRESETS: ReadonlySet<PricePreset> = new Set();

/**
 * Pure presentational view. Renders three toggle buttons in a `flex-wrap`
 * row. The active button uses the solid `--clt-skyline` treatment (matches
 * the v2 filter-rail token table and mirrors `VibeTagPicker`'s active chip).
 * Each button carries `aria-pressed` (not `aria-checked` — these are toggle
 * buttons, not a radio group) so assistive tech announces the on/off state;
 * native `<button>` elements are Tab-reachable and Space/Enter-activatable
 * with no extra wiring. A disabled button uses the native `disabled`
 * attribute (WEB-730) — the browser itself removes it from the tab order and
 * suppresses both pointer and keyboard activation, matching the WEB-659
 * checklist pattern's "not merely dimmed" requirement.
 */
export function DiscoverPricePresetsView({
  value,
  onChange,
  disabledPresets = EMPTY_DISABLED_PRESETS,
  zeroCountLabel = DEFAULT_ZERO_COUNT_LABEL,
  unlistedCount,
  includeUnlisted = false,
  onIncludeUnlistedChange,
  id,
  className,
}: DiscoverPricePresetsViewProps): React.ReactElement {
  const legendId = id ? `${id}-legend` : undefined;
  const showUnlisted =
    onIncludeUnlistedChange !== undefined &&
    shouldShowUnlistedToggle(value, unlistedCount, includeUnlisted);

  return (
    <fieldset
      data-testid="discover-price-presets"
      className={cn('flex flex-col gap-2', className)}
      aria-labelledby={legendId}
    >
      <legend id={legendId} className="text-sm font-semibold text-foreground">
        Price
      </legend>
      <div className="flex flex-wrap gap-2" data-testid="discover-price-presets-options">
        {PRICE_PRESETS.map((preset) => {
          const isActive = value === preset;
          const isDisabled = disabledPresets.has(preset);
          return (
            <button
              key={preset}
              type="button"
              aria-pressed={isActive}
              aria-disabled={isDisabled ? 'true' : undefined}
              disabled={isDisabled}
              data-testid={`discover-price-presets-${preset}`}
              data-active={isActive ? 'true' : 'false'}
              data-disabled={isDisabled ? 'true' : 'false'}
              onClick={() => onChange(isActive ? undefined : preset)}
              className={cn(
                // WEB-1076: 36px paint; `.a-hit-floor` supplies the 44px coarse-pointer hit box.
                'a-hit-floor inline-flex min-h-9 desktop:min-h-0 flex-col items-center justify-center gap-0.5 rounded-full border px-4 text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline focus-visible:ring-offset-2',
                // WEB-745: muted token (4.82:1) instead of opacity-60 on
                // 14px `--foreground` (composites to 3.95:1 on white).
                isDisabled
                  ? 'cursor-not-allowed border-input bg-background text-muted-foreground'
                  : isActive
                    ? 'border-clt-skyline bg-clt-skyline text-white shadow-sm hover:bg-clt-skyline/90'
                    : 'border-input bg-background text-foreground hover:border-clt-skyline hover:bg-clt-skyline-wash',
              )}
            >
              <span className="leading-none">{PRICE_PRESET_LABELS[preset]}</span>
              {isDisabled ? (
                <span className="text-micro leading-none tracking-wide text-muted-foreground/60">
                  {zeroCountLabel}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {showUnlisted ? (
        <div className="flex flex-col gap-1" data-testid="discover-price-unlisted">
          <Chip
            selected={includeUnlisted}
            data-testid="discover-price-unlisted-toggle"
            aria-describedby={id ? `${id}-unlisted-hint` : undefined}
            onClick={() => onIncludeUnlistedChange?.(!includeUnlisted)}
            className="self-start"
          >
            {`+${formatCount(unlistedCount ?? 0)} not listed`}
          </Chip>
          <span
            id={id ? `${id}-unlisted-hint` : undefined}
            className="text-micro text-muted-foreground"
          >
            {includeUnlisted
              ? 'Showing events with no listed price too'
              : 'Events with no listed price are hidden'}
          </span>
        </div>
      ) : null}
    </fieldset>
  );
}

export interface DiscoverPricePresetsProps {
  /**
   * Active city — forwarded to the shared discover facets subscription
   * (WEB-730) so the disabled-zero-count read reflects THIS city's live
   * inventory, not a global/default anchor.
   */
  readonly cityCode: CityCode;
  /** Optional id used to associate the legend with the button group. */
  id?: string;
  /** Wrapper class names. */
  className?: string;
}

/**
 * Connected `DiscoverPricePresets` — reads/writes the `price` URL param via
 * `useDiscoverParams`. `useDiscoverParams` already normalizes any incoming
 * `price` value to a canonical {@link PricePreset} (or clears it) via
 * `parseDiscoverParams` → `normalizePricePresetParam`, including migrating
 * the retired slider's `min-max` / `min+` URL grammar, so this component
 * never has to handle a non-canonical value itself.
 *
 * **Resilience contract (WEB-730, mirrors `DiscoverCategoryFilter`):** presets
 * are disabled ONLY once a CONFIRMED live `facets.priceHistogram` reading is
 * in hand. During loading or on a facets-query error, every preset stays
 * fully interactive — a placeholder/missing signal is not the same as a
 * confirmed "this returns nothing" reading, and disabling on a placeholder
 * would make the control briefly (or on error, indefinitely) unusable.
 */
export function DiscoverPricePresets({
  cityCode,
  id,
  className,
}: DiscoverPricePresetsProps): React.ReactElement {
  const { params, setParams } = useDiscoverParams();

  // Reuse the shared cold-load subscription (same cache key as the list view
  // + results bar via `useDiscoverEventArgs`) — never issues its own request
  // (API-1428).
  const queryArgs = useDiscoverEventArgs(params, cityCode);
  const { data, isError } = useDiscoverFacetsQuery(queryArgs);
  const liveHistogram = !isError ? data?.facets.priceHistogram : undefined;
  // WEB-1230 — same response, no second request. `undefined` while loading,
  // on error, or from a payload that predates the field.
  const unlistedCount = !isError ? data?.facets.priceUnlisted : undefined;
  const includeUnlisted = params.priceUnlisted === true;

  const disabledPresets = React.useMemo<ReadonlySet<PricePreset>>(() => {
    if (liveHistogram === undefined) return EMPTY_DISABLED_PRESETS;
    // With the toggle on, a preset with no listed match still returns the
    // unlisted events, so it is not a dead end.
    if (includeUnlisted && (unlistedCount ?? 0) > 0) return EMPTY_DISABLED_PRESETS;
    const counts = derivePricePresetFacetCounts(liveHistogram);
    const zero = PRICE_PRESETS.filter((preset) => counts[preset] === 0);
    return zero.length > 0 ? new Set(zero) : EMPTY_DISABLED_PRESETS;
  }, [liveHistogram, includeUnlisted, unlistedCount]);

  const handleChange = React.useCallback(
    (next: PricePreset | undefined) => {
      // Clearing the preset clears the toggle too: it means nothing without one.
      setParams(
        next === undefined ? { price: undefined, priceUnlisted: undefined } : { price: next },
      );
    },
    [setParams],
  );

  const handleIncludeUnlistedChange = React.useCallback(
    (next: boolean) => {
      setParams({ priceUnlisted: next ? true : undefined });
    },
    [setParams],
  );

  return (
    <DiscoverPricePresetsView
      id={id}
      value={params.price}
      onChange={handleChange}
      disabledPresets={disabledPresets}
      unlistedCount={unlistedCount}
      includeUnlisted={includeUnlisted}
      onIncludeUnlistedChange={handleIncludeUnlistedChange}
      className={className}
    />
  );
}
